import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  Download,
  FileText,
  HardDrive,
  Loader2,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { useState } from 'react';

interface BackupFile {
  filename: string;
  size: number; // Size in KB
  createdAt: string;
  modifiedAt: string;
}

interface BackupResponse {
  backups: BackupFile[];
}

interface RestoreValidationResponse {
  valid: boolean;
  filename: string;
  fileSize: string;
  createdAt: string;
  tableCount: number;
  message: string;
}

export default function BackupManager() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [selectedBackupForRestore, setSelectedBackupForRestore] =
    useState<string>('');
  const [restoreConfirmation, setRestoreConfirmation] = useState('');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Fetch backups list
  const { data: backupData, isLoading } = useQuery<BackupResponse>({
    queryKey: ['/api/admin/backups'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/backups');
      return response.json();
    },
  });

  // Create backup mutation
  const createBackupMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/admin/backup');
      return response.json();
    },
    onSuccess: data => {
      toast({
        title: 'Backup Created',
        description: `Backup created successfully: ${data.backupFile} (${data.fileSize})`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backups'] });
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Backup Failed',
        description: error.message || 'Failed to create backup',
        variant: 'destructive',
      });
    },
  });

  // Validate backup mutation
  const validateBackupMutation = useMutation<RestoreValidationResponse>({
    mutationFn: async (filename: string) => {
      const response = await apiRequest('POST', '/api/admin/restore/validate', {
        filename,
      });
      return response.json();
    },
    onError: (error: any) => {
      toast({
        title: 'Validation Failed',
        description: error.message || 'Failed to validate backup file',
        variant: 'destructive',
      });
    },
  });

  // Upload backup mutation
  const uploadBackupMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('backup', file);

      const response = await fetch('/api/admin/backups/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload backup');
      }

      return response.json();
    },
    onSuccess: data => {
      toast({
        title: 'Backup Uploaded',
        description: `Backup uploaded successfully: ${data.filename}`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backups'] });
      setIsUploadDialogOpen(false);
      setUploadedFile(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload backup file',
        variant: 'destructive',
      });
    },
  });

  // Restore backup mutation
  const restoreBackupMutation = useMutation({
    mutationFn: async ({
      filename,
      confirmRestore,
    }: {
      filename: string;
      confirmRestore: string;
    }) => {
      console.log('Restore mutation called with:', {
        filename,
        confirmRestore,
      });
      console.log('Making request to: /api/admin/restore');
      console.log('Request body will be:', { filename, confirmRestore });

      const response = await apiRequest('POST', '/api/admin/restore', {
        filename,
        confirmRestore,
      });
      console.log('Response received:', response);
      return response.json();
    },
    onSuccess: data => {
      toast({
        title: 'Database Restored',
        description: `Database restored successfully from ${data.restoredFrom}. Safety backup: ${data.safetyBackup}`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/backups'] });
      setIsRestoreDialogOpen(false);
      setSelectedBackupForRestore('');
      setRestoreConfirmation('');
    },
    onError: (error: any) => {
      toast({
        title: 'Restore Failed',
        description: error.message || 'Failed to restore database',
        variant: 'destructive',
      });
    },
  });

  // Download backup
  const downloadBackup = async (filename: string) => {
    try {
      const response = await fetch(`/api/admin/backups/${filename}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to download backup');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Download Started',
        description: `Downloading ${filename}...`,
      });
    } catch (error: any) {
      toast({
        title: 'Download Failed',
        description: error.message || 'Failed to download backup',
        variant: 'destructive',
      });
    }
  };

  const formatFileSize = (sizeKB: number) => {
    if (sizeKB < 1024) {
      return `${sizeKB} KB`;
    }
    return `${(sizeKB / 1024).toFixed(1)} MB`;
  };

  const handleRestoreBackup = async (filename: string) => {
    setSelectedBackupForRestore(filename);
    setIsRestoreDialogOpen(true);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.sql')) {
        toast({
          title: 'Invalid File Type',
          description: 'Please select a valid SQL backup file (.sql)',
          variant: 'destructive',
        });
        return;
      }

      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Backup file must be smaller than 50MB',
          variant: 'destructive',
        });
        return;
      }

      setUploadedFile(file);
    }
  };

  const uploadBackup = async () => {
    if (!uploadedFile) return;
    await uploadBackupMutation.mutateAsync(uploadedFile);
  };

  const validateAndRestore = async () => {
    if (!selectedBackupForRestore) return;

    console.log('validateAndRestore called with:', {
      selectedBackupForRestore,
      restoreConfirmation,
    });

    try {
      // Use the existing validation mutation instead of direct fetch
      const validation = await validateBackupMutation.mutateAsync(
        selectedBackupForRestore
      );

      if (!validation.valid) {
        toast({
          title: 'Invalid Backup File',
          description: validation.message,
          variant: 'destructive',
        });
        return;
      }

      if (restoreConfirmation !== 'RESTORE') {
        toast({
          title: 'Confirmation Required',
          description:
            'Please type "RESTORE" to confirm the database restore operation',
          variant: 'destructive',
        });
        return;
      }

      console.log('About to call restore mutation with:', {
        filename: selectedBackupForRestore,
        confirmRestore: restoreConfirmation,
      });

      // Temporary direct fetch to debug the issue
      try {
        const response = await fetch('/api/admin/restore', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename: selectedBackupForRestore,
            confirmRestore: restoreConfirmation,
          }),
          credentials: 'include',
        });

        console.log('Direct fetch response status:', response.status);
        const responseText = await response.text();
        console.log('Direct fetch response:', responseText);

        if (!response.ok) {
          throw new Error(`${response.status}: ${responseText}`);
        }

        const data = JSON.parse(responseText);
        console.log('Restore successful:', data);

        toast({
          title: 'Database Restored',
          description: `Database restored successfully from ${data.restoredFrom}. Safety backup: ${data.safetyBackup}`,
        });
        queryClient.invalidateQueries({ queryKey: ['/api/admin/backups'] });
        setIsRestoreDialogOpen(false);
        setSelectedBackupForRestore('');
        setRestoreConfirmation('');
      } catch (error: any) {
        console.error('Direct fetch error:', error);
        throw error;
      }
    } catch (error: any) {
      console.error('Restore validation error:', error);
      toast({
        title: 'Validation Failed',
        description: error.message || 'Failed to validate backup file',
        variant: 'destructive',
      });
    }
  };

  const backups = backupData?.backups || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Database Backup Manager
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Create and manage database backups for your StaffBoard application
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Create Backup
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Database Backup</DialogTitle>
                <DialogDescription>
                  This will create a complete SQL dump of your database
                  including all tables, data, and schema. The backup will be
                  saved to the server and can be downloaded later.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                  disabled={createBackupMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => createBackupMutation.mutate()}
                  disabled={createBackupMutation.isPending}
                  className="flex items-center gap-2"
                >
                  {createBackupMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4" />
                      Create Backup
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={isRestoreDialogOpen}
            onOpenChange={setIsRestoreDialogOpen}
          >
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                Restore Backup
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Restore Database from Backup</DialogTitle>
                <DialogDescription>
                  This will replace all current data with the selected backup. A
                  safety backup will be created automatically before the restore
                  operation.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">
                    Select Backup File
                  </label>
                  <select
                    value={selectedBackupForRestore}
                    onChange={e => setSelectedBackupForRestore(e.target.value)}
                    className="w-full mt-1 p-2 border rounded-md"
                    disabled={restoreBackupMutation.isPending}
                  >
                    <option value="">Choose a backup file...</option>
                    {backups.map(backup => (
                      <option key={backup.filename} value={backup.filename}>
                        {backup.filename} ({formatFileSize(backup.size)}) -{' '}
                        {new Date(backup.createdAt).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedBackupForRestore && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                      <FileText className="h-4 w-4" />
                      <span className="font-medium">Backup Details</span>
                    </div>
                    <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                      <p>
                        <strong>File:</strong> {selectedBackupForRestore}
                      </p>
                      <p>
                        <strong>Size:</strong>{' '}
                        {formatFileSize(
                          backups.find(
                            b => b.filename === selectedBackupForRestore
                          )?.size || 0
                        )}
                      </p>
                      <p>
                        <strong>Created:</strong>{' '}
                        {new Date(
                          backups.find(
                            b => b.filename === selectedBackupForRestore
                          )?.createdAt || ''
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">
                    Type "RESTORE" to confirm this operation
                  </label>
                  <input
                    type="text"
                    value={restoreConfirmation}
                    onChange={e => setRestoreConfirmation(e.target.value)}
                    placeholder="Type RESTORE here..."
                    className="w-full mt-1 p-2 border rounded-md"
                    disabled={restoreBackupMutation.isPending}
                  />
                </div>

                <div className="p-3 bg-red-50 dark:bg-red-950 rounded-md">
                  <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium">Warning</span>
                  </div>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    <p>This operation will:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Replace ALL current database data</li>
                      <li>Create a safety backup before proceeding</li>
                      <li>Cannot be undone without manual intervention</li>
                    </ul>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsRestoreDialogOpen(false);
                    setSelectedBackupForRestore('');
                    setRestoreConfirmation('');
                  }}
                  disabled={restoreBackupMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={validateAndRestore}
                  disabled={
                    restoreBackupMutation.isPending ||
                    !selectedBackupForRestore ||
                    restoreConfirmation !== 'RESTORE'
                  }
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
                >
                  {restoreBackupMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4" />
                      Restore Database
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={isUploadDialogOpen}
            onOpenChange={setIsUploadDialogOpen}
          >
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload Backup
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Backup File</DialogTitle>
                <DialogDescription>
                  Upload a backup file from another PC or external source to
                  restore your database.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">
                    Select Backup File
                  </label>
                  <input
                    type="file"
                    accept=".sql"
                    onChange={handleFileUpload}
                    className="w-full mt-1 p-2 border rounded-md"
                    disabled={uploadBackupMutation.isPending}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Only .sql files are accepted. Maximum file size: 50MB
                  </p>
                </div>

                {uploadedFile && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                      <FileText className="h-4 w-4" />
                      <span className="font-medium">Selected File</span>
                    </div>
                    <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                      <p>
                        <strong>File:</strong> {uploadedFile.name}
                      </p>
                      <p>
                        <strong>Size:</strong>{' '}
                        {formatFileSize(Math.round(uploadedFile.size / 1024))}
                      </p>
                      <p>
                        <strong>Type:</strong>{' '}
                        {uploadedFile.type || 'SQL Backup'}
                      </p>
                    </div>
                  </div>
                )}

                <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-md">
                  <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-medium">Important</span>
                  </div>
                  <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                    <p>Make sure the backup file is:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>A valid StaffBoard backup file</li>
                      <li>Compatible with your current database schema</li>
                      <li>From a trusted source</li>
                    </ul>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsUploadDialogOpen(false);
                    setUploadedFile(null);
                  }}
                  disabled={uploadBackupMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={uploadBackup}
                  disabled={uploadBackupMutation.isPending || !uploadedFile}
                  className="flex items-center gap-2"
                >
                  {uploadBackupMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload Backup
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Backup Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Backups</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{backups.length}</div>
            <p className="text-xs text-muted-foreground">
              {backups.length === 0
                ? 'No backups yet'
                : 'Backup files available'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Latest Backup</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {backups.length > 0
                ? formatDistanceToNow(new Date(backups[0].createdAt), {
                    addSuffix: true,
                  })
                : 'Never'}
            </div>
            <p className="text-xs text-muted-foreground">
              {backups.length > 0 ? 'Most recent backup' : 'No backups created'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Size</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFileSize(
                backups.reduce(
                  (sum: number, backup: BackupFile) => sum + backup.size,
                  0
                )
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              All backup files combined
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Backups List */}
      <Card>
        <CardHeader>
          <CardTitle>Available Backups</CardTitle>
          <CardDescription>
            Click download to save a backup file to your computer
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading backups...</span>
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8">
              <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                No backups yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create your first database backup to get started
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Database className="h-4 w-4 mr-2" />
                Create First Backup
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((backup: BackupFile) => (
                  <TableRow key={backup.filename}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4 text-gray-500" />
                        {backup.filename}
                      </div>
                    </TableCell>
                    <TableCell>{formatFileSize(backup.size)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {new Date(backup.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(backup.createdAt), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        <CheckCircle className="h-3 w-3" />
                        Ready
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadBackup(backup.filename)}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreBackup(backup.filename)}
                          className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Restore
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <AlertTriangle className="h-5 w-5" />
            Important Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="text-amber-700 dark:text-amber-300">
          <ul className="space-y-2 text-sm">
            <li>• Backups include all database tables, data, and schema</li>
            <li>
              • Backup files are stored on the server and can be downloaded
            </li>
            <li>• Regular backups are recommended for data protection</li>
            <li>• Keep backup files in a secure location outside the server</li>
            <li>
              • Test restore procedures periodically to ensure backup integrity
            </li>
            <li>
              • Restore operations create automatic safety backups before
              proceeding
            </li>
            <li>
              • Restore will replace ALL current data - use with extreme caution
            </li>
            <li>• Always verify backup file integrity before restoring</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
